import React, { useState, useEffect } from 'react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ToastProvider';

// #312: Reiter „Feedback“ des Admin-Bereichs, Code unverändert aus
// AdminPanel.js übernommen (Muster und Begründung: admin/LogsTab.js).
//
// `setError` kommt vom Panel: Fehler dieses Reiters erscheinen wie bisher in
// der gemeinsamen Fehlermeldung oberhalb der Reiter.
export function useFeedbackTab({ setError }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  // Feedback (#42)
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState('neu');

  const loadFeedback = async (filter) => {
    const status = filter !== undefined ? filter : feedbackFilter;
    setFeedbackLoading(true);
    try {
      const resp = await apiService.getFeedbackAdmin(status === 'alle' ? null : status);
      setFeedbackList(resp.feedback || []);
    } catch (e) {
      console.error('Error loading feedback:', e);
      setError(t('admin.feedback.loadError', 'Fehler beim Laden des Feedbacks.'));
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleUpdateFeedback = async (id, updates) => {
    setError(null);
    try {
      await apiService.updateFeedbackAdmin(id, updates);
      pushToast({ message: t('admin.feedback.updated', 'Feedback aktualisiert.'), type: 'success' });
      await loadFeedback();
    } catch (e) {
      console.error('Error updating feedback:', e);
      setError(e.response?.data?.error || t('admin.feedback.updateError', 'Fehler beim Aktualisieren des Feedbacks.'));
    }
  };

  // #83: Eintrag nach Rückfrage endgültig löschen
  const handleDeleteFeedback = async (entry) => {
    const ok = await confirm({
      title: t('admin.feedback.deleteTitle', 'Feedback löschen?'),
      message: t('admin.feedback.deleteConfirm', 'Diesen Eintrag endgültig löschen? Das kann nicht rückgängig gemacht werden.'),
      confirmLabel: t('common.delete', 'Löschen'),
      danger: true
    });
    if (!ok) return;
    setError(null);
    try {
      await apiService.deleteFeedbackAdmin(entry.id);
      pushToast({ message: t('admin.feedback.deleted', 'Feedback gelöscht.'), type: 'success' });
      await loadFeedback();
    } catch (e) {
      console.error('Error deleting feedback:', e);
      setError(e.response?.data?.error || t('admin.feedback.deleteError', 'Fehler beim Löschen des Feedbacks.'));
    }
  };

  return { feedbackList, feedbackLoading, feedbackFilter, setFeedbackFilter, loadFeedback, handleUpdateFeedback, handleDeleteFeedback };
}

const FeedbackTab = ({ tab }) => {
  const { getLocale, t } = useI18n();
  const { feedbackList, feedbackLoading, feedbackFilter, setFeedbackFilter, loadFeedback, handleUpdateFeedback, handleDeleteFeedback } = tab;

  return (
    <div className="admin-section">
      <h3>{t('admin.feedback.title', 'Feedback')}</h3>
      <p className="section-description">
        {t('admin.feedback.intro', 'Rückmeldungen aus dem Feedback-Button im Header. Nach Übernahme in ein Issue den Status auf „uebernommen" setzen und die Issue-Nummer eintragen.')}
      </p>
      <div className="form-group" style={{ maxWidth: 260 }}>
        <label>{t('admin.feedback.filter', 'Status-Filter')}</label>
        <select
          className="form-input"
          value={feedbackFilter}
          onChange={(e) => { setFeedbackFilter(e.target.value); loadFeedback(e.target.value); }}
        >
          {['neu', 'gesichtet', 'uebernommen', 'abgelehnt', 'alle'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
      {feedbackLoading ? (
        <p>{t('common.loading', 'Lade...')}</p>
      ) : feedbackList.length === 0 ? (
        <p className="section-description">{t('admin.feedback.empty', 'Keine Einträge für diesen Filter.')}</p>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>{t('admin.feedback.date', 'Datum')}</th>
                <th>{t('admin.feedback.type', 'Typ')}</th>
                <th>{t('admin.feedback.reporter', 'Melder')}</th>
                <th>{t('admin.feedback.message', 'Nachricht')}</th>
                <th>{t('admin.feedback.screenshot', 'Screenshot')}</th>
                <th>{t('admin.feedback.status', 'Status')}</th>
                <th>{t('admin.feedback.issue', 'Issue #')}</th>
                <th>{t('admin.feedback.actions', 'Aktionen')}</th>
              </tr>
            </thead>
            <tbody>
              {feedbackList.map(f => (
                <FeedbackRow key={f.id} entry={f} onUpdate={handleUpdateFeedback} onDelete={handleDeleteFeedback} t={t} getLocale={getLocale} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Eine Feedback-Zeile mit eigenem Bearbeitungs-State (#42): Status speichert
// sofort, Issue-Nr. und Notiz beim Verlassen des Feldes (nur bei Änderung).
// #83: Nachricht/Typ nachträglich editierbar + Eintrag löschbar.
const FeedbackRow = ({ entry, onUpdate, onDelete, t, getLocale }) => {
  const [issueNumber, setIssueNumber] = useState(entry.issueNumber != null ? String(entry.issueNumber) : '');
  const [note, setNote] = useState(entry.note || '');
  const [editing, setEditing] = useState(false);
  const [editMessage, setEditMessage] = useState(entry.message);
  const [editType, setEditType] = useState(entry.type);

  // useState-Initialwerte gelten nur beim Mount — wenn die Liste nach einem
  // Update neu geladen wird, behält die Zeile sonst veraltete Werte (#124).
  useEffect(() => {
    setIssueNumber(entry.issueNumber != null ? String(entry.issueNumber) : '');
    setNote(entry.note || '');
  }, [entry.issueNumber, entry.note]);

  const saveIssueNumber = () => {
    const parsed = issueNumber.trim() === '' ? null : parseInt(issueNumber, 10);
    if (issueNumber.trim() !== '' && !Number.isInteger(parsed)) return;
    if (parsed === (entry.issueNumber != null ? entry.issueNumber : null)) return;
    onUpdate(entry.id, { issueNumber: parsed });
  };

  const saveNote = () => {
    if ((note || '').trim() === (entry.note || '')) return;
    onUpdate(entry.id, { note });
  };

  const startEdit = () => {
    setEditMessage(entry.message);
    setEditType(entry.type);
    setEditing(true);
  };

  const saveEdit = () => {
    if (!editMessage.trim()) return;
    const updates = {};
    if (editMessage.trim() !== entry.message) updates.message = editMessage;
    if (editType !== entry.type) updates.type = editType;
    if (Object.keys(updates).length > 0) onUpdate(entry.id, updates);
    setEditing(false);
  };

  return (
    <tr>
      <td>{new Date(entry.createdAt).toLocaleString(getLocale())}</td>
      <td>
        {editing ? (
          <select className="form-input" value={editType} onChange={(e) => setEditType(e.target.value)}>
            <option value="bug">{t('feedback.typeBug', 'Fehler')}</option>
            <option value="wish">{t('feedback.typeWish', 'Wunsch')}</option>
          </select>
        ) : (
          entry.type === 'wish' ? t('feedback.typeWish', 'Wunsch') : t('feedback.typeBug', 'Fehler')
        )}
      </td>
      <td title={entry.userEmail || ''}>{entry.userName || entry.userEmail || '—'}</td>
      <td style={{ maxWidth: 380 }}>
        {editing ? (
          <textarea
            className="form-textarea"
            rows="3"
            value={editMessage}
            onChange={(e) => setEditMessage(e.target.value)}
            autoFocus
          />
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{entry.message}</div>
        )}
        {entry.contextUrl && (
          <small style={{ color: 'var(--color-text-muted)' }}>{entry.contextUrl}</small>
        )}
        <textarea
          className="form-textarea"
          rows="1"
          style={{ marginTop: 6 }}
          placeholder={t('admin.feedback.notePlaceholder', 'Triage-Notiz…')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
        />
      </td>
      <td>
        {/* #575: mehrere Bilder als 1, 2, 3 …; ein Bild wie bisher „ansehen“ */}
        {(entry.screenshotCount || (entry.hasScreenshot ? 1 : 0)) > 1 ? (
          <span className="feedback-bild-links">
            {Array.from({ length: entry.screenshotCount }, (_, i) => (
              <a
                key={i}
                href={`/api/admin/feedback/${entry.id}/screenshot/${i + 1}`}
                target="_blank"
                rel="noreferrer"
                title={t('admin.feedback.viewNr', 'Screenshot {{nr}} ansehen', { nr: i + 1 })}
                aria-label={t('admin.feedback.viewNr', 'Screenshot {{nr}} ansehen', { nr: i + 1 })}
              >
                {i + 1}
              </a>
            ))}
          </span>
        ) : entry.hasScreenshot ? (
          <a href={`/api/admin/feedback/${entry.id}/screenshot`} target="_blank" rel="noreferrer">
            {t('admin.feedback.view', 'ansehen')}
          </a>
        ) : '—'}
      </td>
      <td>
        <select
          className="form-input"
          value={entry.status}
          onChange={(e) => onUpdate(entry.id, { status: e.target.value })}
        >
          {['neu', 'gesichtet', 'uebernommen', 'abgelehnt'].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </td>
      <td>
        <input
          className="form-input"
          style={{ width: 80 }}
          value={issueNumber}
          onChange={(e) => setIssueNumber(e.target.value)}
          onBlur={saveIssueNumber}
          placeholder="—"
        />
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {editing ? (
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={saveEdit} disabled={!editMessage.trim()}>
              {t('common.save', 'Speichern')}
            </button>{' '}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
              {t('common.cancel', 'Abbrechen')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-secondary btn-sm" onClick={startEdit}>
              {t('common.edit', 'Bearbeiten')}
            </button>{' '}
            <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(entry)}>
              {t('common.delete', 'Löschen')}
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

export default FeedbackTab;
